/** @format */
import { IconSize } from '@mpb-com/feather/components/atoms/icon';
import { useTranslation } from 'next-i18next';
import React, { useContext } from 'react';
import styled from 'styled-components';

import CheckmarkIcon from '@toucan/client/global/icons/checkmark.svg';
import CloseIcon from '@toucan/client/global/icons/close.svg';
import NotesIcon from '@toucan/client/global/icons/notes.svg';

import { MarketContext } from '@toucan/client/global/market';
import {
    getAttributeTestId,
    isConditionSparesAndRepairs,
    isUsageAttribute,
    preparePriceModifiers,
} from '@toucan/client/global/product';

import { CardBackground } from '@toucan/client/shared/components/card-background';

import { FavouriteProductButton } from '@toucan/client/features/search';

import { splitLines } from '@toucan/client/views/product/components/product-details/helpers/text';
import { ProductPriceModifiers } from '@toucan/client/views/product/components/product-price-modifiers';

import { ProductAttributeListItem } from '../product-attribute-list-item';
import { ProductConditionListItem } from '../product-condition-list-item';
import { ProductDetailsListItem } from '../product-details-list-item';
import { ProductObservationsListItem } from '../product-observations-list-item';
import { ProductSupportingImagesListItem } from '../product-supporting-images-list-item';
import { DEFAULT_TEST_ID } from './constants';
import { styles } from './product-details.styles';
import { ProductDetailsProps } from './types';

const StyledProductDetails = styled(CardBackground)`
    ${styles}
`;

export const ProductDetails = ({
    productInfo,
    'data-testid': dataTestId = DEFAULT_TEST_ID,
}: ProductDetailsProps) => {
    const { activeMarket } = useContext(MarketContext);
    const { t } = useTranslation();
    const {
        id,
        sku,
        condition,
        conditionDescription,
        observations,
        priceModifiers,
        attributes,
        internalImages,
        customDescription,
    } = productInfo;

    // eslint-disable-next-line @typescript-eslint/naming-convention -- Legacy code: do not use as an example.
    const showCustomDescription =
        isConditionSparesAndRepairs(condition) && Boolean(customDescription);
    const hasObservations = observations.length > 0;
    const hasSupportingImages = internalImages.length > 0;

    const { included, notIncluded } = preparePriceModifiers({
        priceModifiers,
        condition,
        activeMarket,
        t,
    });

    function formatCustomDescription() {
        return splitLines(customDescription).map((line, index) => (
            // eslint-disable-next-line react/no-array-index-key -- Legacy code: do not use as an example.
            <p key={index}>{line}</p>
        ));
    }

    return (
        <StyledProductDetails
            expandOnHover={false}
            className="product-details"
            data-testid={dataTestId}
        >
            <div
                className="product-details__sku-container"
                data-testid={`${dataTestId}__sku`}
            >
                <p className="product-details__sku">
                    {t('Additional product information | SKU:')} {sku}
                </p>
                <FavouriteProductButton
                    productId={id}
                    isLoading={false}
                    size={IconSize.S}
                />
            </div>
            <ul className="product-details__list">
                <ProductConditionListItem
                    className="product-details__condition"
                    condition={condition}
                    conditionDescription={conditionDescription}
                    data-testid={`${dataTestId}__condition`}
                />
                {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                {showCustomDescription && (
                    <ProductDetailsListItem
                        data-testid={`${dataTestId}__custom-description`}
                        title={formatCustomDescription()}
                        icon={<NotesIcon />}
                    />
                )}
                {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                {hasObservations && (
                    <ProductObservationsListItem
                        data-testid={`${dataTestId}__observations`}
                        className="product-details__observations"
                        observations={observations}
                    />
                )}
                {/* eslint-disable-next-line react/jsx-no-leaked-render -- Legacy code: do not use as an example. */}
                {hasSupportingImages && (
                    <ProductSupportingImagesListItem
                        data-testid={`${dataTestId}__supporting-images`}
                        className="list-item-supporting-images"
                        supportingImages={internalImages}
                    />
                )}
                {included.length > 0 && (
                    <ProductPriceModifiers
                        data-testid={`${dataTestId}__included`}
                        className="product-details-list__included__price-modifiers"
                        title={t("What's included")}
                        icon={<CheckmarkIcon />}
                        priceModifiers={included}
                    />
                )}
                {notIncluded.length > 0 && (
                    <ProductPriceModifiers
                        data-testid={`${dataTestId}__not-included`}
                        title={t("What's not included")}
                        icon={<CloseIcon />}
                        priceModifiers={notIncluded}
                    />
                )}
                {attributes.map(({ name, ...otherProps }) => {
                    if (!isUsageAttribute(name) && otherProps.content === '') {
                        return null;
                    }

                    return (
                        <div
                            data-testid={`${dataTestId}__attributes`}
                            key={name}
                        >
                            <ProductAttributeListItem
                                data-testid={`${dataTestId}__${getAttributeTestId(
                                    name
                                )}-attribute`}
                                className="list-item-attribute"
                                attribute={{ name, ...otherProps }}
                            />
                        </div>
                    );
                })}
            </ul>
        </StyledProductDetails>
    );
};
